import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Plus, Edit, Trash2, AlertTriangle, X, FileText, MessageCircle, Upload, ExternalLink, Calendar, FileCheck, Eye, DollarSign, Send, Check, Link, Download } from 'lucide-react';
import { toast } from 'sonner';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import { usePropiedad } from '../contexts/PropiedadContext';

// Imported components
import {
  ReservaFilters,
  ReservaTable,
  ReservaForm,
  ClienteFormModal,
  ContratoUploadModal,
  ArchivoUploadModal,
  DocumentoPreviewModal
} from '../components/reservas';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Reservas = () => {
  const { propiedadSeleccionada } = usePropiedad();
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const clienteFiltro = searchParams.get('cliente');
  const reservaIdParam = searchParams.get('reserva');
  
  // Data states
  const [reservas, setReservas] = useState([]);
  const [canales, setCanales] = useState([]);
  const [clientes, setClientes] = useState([]);
  const [categorias, setCategorias] = useState([]);
  const [proveedores, setProveedores] = useState([]);
  const [estadosReserva, setEstadosReserva] = useState([]);
  
  // Modal states
  const [modalAbierto, setModalAbierto] = useState(false);
  const [modalClienteAbierto, setModalClienteAbierto] = useState(false);
  const [modalContratoAbierto, setModalContratoAbierto] = useState(false);
  const [modalDetalleAbierto, setModalDetalleAbierto] = useState(false);
  const [modalSubirArchivo, setModalSubirArchivo] = useState(false);
  const [modalPreviewDocumento, setModalPreviewDocumento] = useState(false);
  const [modalAsociarReserva, setModalAsociarReserva] = useState(false);
  
  // Editing states
  const [reservaEditando, setReservaEditando] = useState(null);
  const [reservaAEliminar, setReservaAEliminar] = useState(null);
  const [reservaDetalle, setReservaDetalle] = useState(null);
  const [reservaParaContrato, setReservaParaContrato] = useState(null);
  const [clienteDetalle, setClienteDetalle] = useState(null);
  
  // Filter states
  const [busqueda, setBusqueda] = useState(clienteFiltro || '');
  const [filtroAnio, setFiltroAnio] = useState(new Date().getFullYear().toString());
  const [filtroEstado, setFiltroEstado] = useState('');
  const [filtroFechaDesde, setFiltroFechaDesde] = useState('');
  const [filtroFechaHasta, setFiltroFechaHasta] = useState('');
  
  // Leer filtros de fecha desde URL params
  useEffect(() => {
    const desde = searchParams.get('desde');
    const hasta = searchParams.get('hasta');
    if (desde) {
      setFiltroFechaDesde(desde);
      // Extraer el año del filtro para ajustar el filtro de año
      const anioDesde = desde.split('-')[0];
      setFiltroAnio(anioDesde);
    }
    if (hasta) {
      setFiltroFechaHasta(hasta);
    }
  }, [searchParams]);
  
  // Form states
  const [clienteSeleccionado, setClienteSeleccionado] = useState('');
  const [nuevoCliente, setNuevoCliente] = useState({ nombre: '', telefono: '', email: '', notas: '' });
  const [gastosReserva, setGastosReserva] = useState([]);
  const [formData, setFormData] = useState({
    propiedad_id: '',
    huesped_nombre: '',
    huesped_telefono: '',
    huesped_email: '',
    canal_id: '',
    fecha_check_in: '',
    fecha_check_out: '',
    estado_reserva: 'Confirmada',
    valor_noche: 0,
    monto_bruto: 0,
    comisiones: 0,
    notas: '',
    monto_abono: 0,
    porcentaje_abono: 0,
    cliente_id: ''
  });
  
  // Detail modal states
  const [editandoEstado, setEditandoEstado] = useState(false);
  const [nuevoEstado, setNuevoEstado] = useState('');
  const [editandoCanal, setEditandoCanal] = useState(false);
  const [nuevoCanal, setNuevoCanal] = useState('');
  const [nuevoAbono, setNuevoAbono] = useState({ monto: 0, fecha: new Date().toISOString().split('T')[0] });
  const [editandoAbonoId, setEditandoAbonoId] = useState(null);
  const [mensajesHistorial, setMensajesHistorial] = useState([]);
  const [editandoNombreMensaje, setEditandoNombreMensaje] = useState(null);
  const [nombreMensajeTemporal, setNombreMensajeTemporal] = useState('');
  const [mensajeEliminar, setMensajeEliminar] = useState(null);
  const [mensajeAsociar, setMensajeAsociar] = useState(null);
  const [reservaSeleccionadaAsociar, setReservaSeleccionadaAsociar] = useState('');
  
  // Upload states
  const [archivoContrato, setArchivoContrato] = useState(null);
  const [subiendoContrato, setSubiendoContrato] = useState(false);
  const [archivoAdicional, setArchivoAdicional] = useState(null);
  const [nombreArchivoAdicional, setNombreArchivoAdicional] = useState('');
  const [subiendoArchivoAdicional, setSubiendoArchivoAdicional] = useState(false);
  const [documentoPreview, setDocumentoPreview] = useState(null);

  // Utility functions
  const formatCurrency = (value) => {
    return `$${Math.round(value || 0).toString().replace(/\B(?=(\d{3})+(?!\d))/g, '.')}`;
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr + 'T12:00:00');
    return date.toLocaleDateString('es-CL', { day: '2-digit', month: 'short', year: 'numeric' });
  };

  const getColorEstado = (estado) => {
    const estadoConfig = estadosReserva.find(e => e.nombre === estado);
    if (estadoConfig) {
      return { fondo: estadoConfig.color, texto: estadoConfig.color_texto || '#FFFFFF' };
    }
    return { fondo: '#8FE0FF', texto: '#0E3A53' };
  };

  const obtenerProveedoresPorCategoria = (categoriaId) => {
    return proveedores.filter(p => p.categoria_id === categoriaId);
  };

  const calcularTotalAbonos = (abonos) => {
    if (!abonos || !Array.isArray(abonos)) return 0;
    return abonos.reduce((sum, a) => sum + (a.monto || 0), 0);
  };

  // Data loading
  const cargarReservas = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/reservas`, {
        params: { propiedad_id: propiedadSeleccionada?.id_propiedad }
      });
      setReservas(response.data);
    } catch (error) {
      toast.error('Error al cargar reservas');
      console.error(error);
    }
  }, [propiedadSeleccionada]);

  const cargarDatos = useCallback(async () => {
    if (!propiedadSeleccionada) return;
    try {
      const [resCanales, resClientes, resCategorias, resProveedores, resEstados] = await Promise.all([
        axios.get(`${API}/canales`),
        axios.get(`${API}/clientes?propiedad_id=${propiedadSeleccionada.id_propiedad}`),
        axios.get(`${API}/categorias`),
        axios.get(`${API}/proveedores`),
        axios.get(`${API}/estados-reserva`)
      ]);
      setCanales(resCanales.data);
      setClientes(resClientes.data);
      setCategorias(resCategorias.data);
      setProveedores(resProveedores.data);
      setEstadosReserva(resEstados.data);
      if (propiedadSeleccionada?.id_propiedad) {
        setFormData(prev => ({ ...prev, propiedad_id: propiedadSeleccionada.id_propiedad }));
      }
    } catch (error) {
      toast.error('Error al cargar datos');
      console.error(error);
    }
  }, [propiedadSeleccionada]);

  useEffect(() => {
    cargarDatos();
  }, [cargarDatos]);

  useEffect(() => {
    if (propiedadSeleccionada?.id_propiedad) {
      cargarReservas();
    }
  }, [propiedadSeleccionada, cargarReservas]);

  useEffect(() => {
    if (reservaIdParam && reservas.length > 0) {
      const reserva = reservas.find(r => r.id_reserva === reservaIdParam);
      if (reserva) {
        abrirModalDetalle(reserva);
        setSearchParams(prev => {
          const newParams = new URLSearchParams(prev);
          newParams.delete('reserva');
          return newParams;
        });
      }
    }
  }, [reservaIdParam, reservas, setSearchParams]);

  // Filtering
  const reservasOrdenadas = [...reservas].sort((a, b) => {
    return new Date(b.fecha_check_in) - new Date(a.fecha_check_in);
  });

  const aniosDisponibles = [...new Set(reservas.map(r => new Date(r.fecha_check_in).getFullYear()))].sort((a, b) => b - a);

  const reservasFiltradas = reservasOrdenadas.filter(r => {
    if (busqueda && !r.huesped_nombre.toLowerCase().includes(busqueda.toLowerCase())) return false;
    if (filtroAnio && filtroAnio !== 'todos' && new Date(r.fecha_check_in).getFullYear() !== parseInt(filtroAnio)) return false;
    if (filtroEstado && filtroEstado !== 'todos' && r.estado_reserva !== filtroEstado) return false;
    
    // Filtro por rango de fechas (reservas que se solapan con el rango)
    if (filtroFechaDesde || filtroFechaHasta) {
      const checkIn = new Date(r.fecha_check_in);
      const checkOut = new Date(r.fecha_check_out);
      const desde = filtroFechaDesde ? new Date(filtroFechaDesde) : null;
      const hasta = filtroFechaHasta ? new Date(filtroFechaHasta) : null;
      
      // La reserva debe solaparse con el rango: check_in <= hasta Y check_out >= desde
      if (desde && checkOut < desde) return false;
      if (hasta && checkIn > hasta) return false;
    }
    
    return true;
  });

  const limpiarFiltros = () => {
    setBusqueda('');
    setFiltroAnio(new Date().getFullYear().toString());
    setFiltroEstado('');
    setFiltroFechaDesde('');
    setFiltroFechaHasta('');
    setSearchParams({});
  };

  // Modal handlers
  const abrirModal = async (reserva = null) => {
    if (reserva) {
      setReservaEditando(reserva);
      const clienteExistente = clientes.find(c => c.nombre.toLowerCase() === reserva.huesped_nombre.toLowerCase());
      setClienteSeleccionado(clienteExistente?.id_cliente || '');
      setFormData({
        propiedad_id: reserva.propiedad_id,
        huesped_nombre: reserva.huesped_nombre,
        huesped_telefono: reserva.huesped_telefono || '',
        huesped_email: reserva.huesped_email || '',
        canal_id: reserva.canal_id,
        fecha_check_in: reserva.fecha_check_in,
        fecha_check_out: reserva.fecha_check_out,
        estado_reserva: reserva.estado_reserva,
        monto_bruto: reserva.monto_bruto,
        comisiones: reserva.comisiones,
        notas: reserva.notas || '',
        monto_abono: reserva.monto_abono || 0,
        porcentaje_abono: reserva.porcentaje_abono || 0
      });
      try {
        const resGastos = await axios.get(`${API}/gastos/reserva/${reserva.id_reserva}`);
        setGastosReserva(resGastos.data);
      } catch (error) {
        setGastosReserva([]);
      }
    } else {
      setReservaEditando(null);
      setClienteSeleccionado('');
      setGastosReserva([]);
      setFormData({
        propiedad_id: propiedadSeleccionada?.id_propiedad || '',
        huesped_nombre: '',
        huesped_telefono: '',
        huesped_email: '',
        canal_id: canales[0]?.id_canal || '',
        fecha_check_in: '',
        fecha_check_out: '',
        estado_reserva: 'Confirmada',
        monto_bruto: 0,
        comisiones: 0,
        notas: '',
        monto_abono: 0,
        porcentaje_abono: 0
      });
    }
    setModalAbierto(true);
  };

  const abrirModalDetalle = async (reserva) => {
    setReservaDetalle(reserva);
    setNuevoEstado(reserva.estado_reserva);
    setEditandoEstado(false);
    setModalDetalleAbierto(true);

    try {
      let cliente = null;
      if (reserva.cliente_id) {
        const clienteResponse = await axios.get(`${API}/clientes?propiedad_id=${propiedadSeleccionada?.id_propiedad}`);
        cliente = clienteResponse.data.find(c => c.id_cliente === reserva.cliente_id);
      }
      if (!cliente) {
        const clientesResponse = await axios.get(`${API}/clientes?propiedad_id=${propiedadSeleccionada?.id_propiedad}`);
        const nombreReserva = reserva.huesped_nombre.toLowerCase().trim();
        cliente = clientesResponse.data.find(c => {
          const nombreCompleto = `${c.nombre} ${c.apellido || ''}`.toLowerCase().trim();
          return nombreCompleto === nombreReserva || c.nombre.toLowerCase().trim() === nombreReserva;
        });
      }
      setClienteDetalle(cliente);
    } catch (error) {
      setClienteDetalle(null);
    }

    try {
      const mensajesResponse = await axios.get(`${API}/reservas/${reserva.id_reserva}/mensajes`);
      setMensajesHistorial(mensajesResponse.data);
    } catch (error) {
      setMensajesHistorial([]);
    }
  };

  // CRUD operations
  const seleccionarCliente = (clienteId) => {
    setClienteSeleccionado(clienteId);
    if (clienteId) {
      const cliente = clientes.find(c => c.id_cliente === clienteId);
      if (cliente) {
        const nombreCompleto = `${cliente.nombre}${cliente.apellido ? ' ' + cliente.apellido : ''}`;
        setFormData({
          ...formData,
          huesped_nombre: nombreCompleto,
          huesped_telefono: cliente.telefono || '',
          huesped_email: cliente.email || '',
          cliente_id: clienteId
        });
      }
    }
  };

  const abrirModalNuevoCliente = () => {
    setNuevoCliente({ 
      propiedad_id: propiedadSeleccionada?.id_propiedad || '',
      nombre: '', 
      telefono: '', 
      email: '', 
      notas: '' 
    });
    setModalClienteAbierto(true);
  };

  const guardarNuevoCliente = async () => {
    try {
      const clienteData = {
        ...nuevoCliente,
        propiedad_id: propiedadSeleccionada?.id_propiedad || nuevoCliente.propiedad_id
      };
      const response = await axios.post(`${API}/clientes`, clienteData);
      toast.success('Cliente creado exitosamente');
      await cargarDatos();
      setClienteSeleccionado(response.data.id_cliente);
      setFormData({
        ...formData,
        huesped_nombre: response.data.nombre,
        huesped_telefono: response.data.telefono || '',
        huesped_email: response.data.email || ''
      });
      setModalClienteAbierto(false);
    } catch (error) {
      toast.error('Error al crear cliente');
    }
  };

  // Expense handlers
  const agregarGastoReserva = () => {
    setGastosReserva([...gastosReserva, {
      id_temp: Date.now(),
      categoria_id: categorias[0]?.id_categoria || '',
      proveedor: '',
      proveedor_id: '',
      monto: 0,
      notas: ''
    }]);
  };

  const actualizarGastoReserva = (index, campo, valor) => {
    const nuevosGastos = [...gastosReserva];
    nuevosGastos[index][campo] = valor;
    if (campo === 'categoria_id') {
      nuevosGastos[index].proveedor = '';
      nuevosGastos[index].proveedor_id = '';
    }
    if (campo === 'proveedor_id' && valor) {
      const prov = proveedores.find(p => p.id_proveedor === valor);
      if (prov) nuevosGastos[index].proveedor = prov.nombre;
    }
    setGastosReserva(nuevosGastos);
  };

  const eliminarGastoReserva = async (index) => {
    const gasto = gastosReserva[index];
    if (gasto.id_gasto) {
      try {
        await axios.delete(`${API}/gastos/${gasto.id_gasto}`);
        toast.success('Gasto eliminado');
      } catch (error) {
        toast.error('Error al eliminar gasto');
        return;
      }
    }
    setGastosReserva(gastosReserva.filter((_, i) => i !== index));
  };

  const guardarReserva = async () => {
    if (!reservaEditando && !clienteSeleccionado) {
      toast.error('Debes seleccionar un cliente antes de crear la reserva');
      return;
    }

    try {
      let reservaId;
      if (reservaEditando) {
        await axios.put(`${API}/reservas/${reservaEditando.id_reserva}`, formData);
        reservaId = reservaEditando.id_reserva;
        toast.success('Reserva actualizada');
      } else {
        const response = await axios.post(`${API}/reservas`, formData);
        reservaId = response.data.id_reserva;
        toast.success('Reserva creada');
      }
      
      for (const gasto of gastosReserva) {
        if (!gasto.id_gasto && gasto.categoria_id && gasto.monto > 0) {
          await axios.post(`${API}/gastos`, {
            propiedad_id: formData.propiedad_id,
            fecha: formData.fecha_check_in,
            categoria_id: gasto.categoria_id,
            proveedor: gasto.proveedor || 'Sin proveedor',
            proveedor_id: gasto.proveedor_id || null,
            monto: gasto.monto,
            notas: gasto.notas || '',
            reserva_id: reservaId
          });
        }
      }
      
      cargarReservas();
      cargarDatos();
      setModalAbierto(false);
      setClienteSeleccionado('');
      setGastosReserva([]);
    } catch (error) {
      toast.error('Error al guardar reserva');
    }
  };

  const confirmarEliminacion = (reserva) => setReservaAEliminar(reserva);

  const eliminarReserva = async () => {
    if (!reservaAEliminar) return;
    try {
      await axios.delete(`${API}/reservas/${reservaAEliminar.id_reserva}`);
      toast.success('Reserva eliminada exitosamente');
      setReservaAEliminar(null);
      cargarDatos();
    } catch (error) {
      toast.error('Error al eliminar la reserva');
    }
  };

  // Detail modal operations
  const actualizarEstadoReserva = async () => {
    if (!reservaDetalle) return;
    try {
      await axios.patch(`${API}/reservas/${reservaDetalle.id_reserva}/estado?estado=${nuevoEstado}`);
      toast.success('Estado actualizado correctamente');
      setEditandoEstado(false);
      cargarReservas();
      setReservaDetalle({ ...reservaDetalle, estado_reserva: nuevoEstado });
    } catch (error) {
      toast.error('Error al actualizar el estado');
    }
  };

  const actualizarCanalReserva = async () => {
    if (!reservaDetalle || !nuevoCanal) return;
    try {
      await axios.patch(`${API}/reservas/${reservaDetalle.id_reserva}/canal?canal_id=${nuevoCanal}`);
      toast.success('Canal actualizado correctamente');
      setEditandoCanal(false);
      cargarReservas();
      setReservaDetalle({ ...reservaDetalle, canal_id: nuevoCanal });
    } catch (error) {
      toast.error('Error al actualizar el canal');
    }
  };

  const agregarAbono = async () => {
    if (!reservaDetalle || nuevoAbono.monto <= 0) {
      toast.error('Ingrese un monto válido');
      return;
    }
    try {
      const response = await axios.post(`${API}/reservas/${reservaDetalle.id_reserva}/abono`, nuevoAbono);
      toast.success('Abono agregado correctamente');
      setNuevoAbono({ monto: 0, fecha: new Date().toISOString().split('T')[0] });
      cargarReservas();
      setReservaDetalle(response.data);
    } catch (error) {
      toast.error('Error al agregar el abono');
    }
  };

  const editarAbono = async (idAbono, monto, fecha) => {
    if (!reservaDetalle) return;
    try {
      const response = await axios.put(`${API}/reservas/${reservaDetalle.id_reserva}/abono/${idAbono}`, { monto, fecha });
      toast.success('Abono actualizado');
      setEditandoAbonoId(null);
      cargarReservas();
      setReservaDetalle(response.data);
    } catch (error) {
      toast.error('Error al editar el abono');
    }
  };

  const eliminarAbono = async (idAbono) => {
    if (!reservaDetalle) return;
    try {
      await axios.delete(`${API}/reservas/${reservaDetalle.id_reserva}/abono/${idAbono}`);
      toast.success('Abono eliminado');
      cargarReservas();
      const abonos = (reservaDetalle.abonos || []).filter(a => a.id_abono !== idAbono);
      setReservaDetalle({ ...reservaDetalle, abonos });
    } catch (error) {
      toast.error('Error al eliminar el abono');
    }
  };

  // File operations
  const abrirModalContrato = (reserva) => {
    setReservaParaContrato(reserva);
    setArchivoContrato(null);
    setModalContratoAbierto(true);
  };

  const subirContratoFirmado = async () => {
    if (!archivoContrato || !reservaParaContrato) return;
    try {
      setSubiendoContrato(true);
      const formDataUpload = new FormData();
      formDataUpload.append('file', archivoContrato);
      
      const response = await axios.post(`${API}/upload-contrato-firmado`, formDataUpload, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      const reservaActualizada = { ...reservaParaContrato, contrato_firmado_url: response.data.url };
      await axios.put(`${API}/reservas/${reservaParaContrato.id_reserva}`, reservaActualizada);

      toast.success('Contrato firmado adjuntado exitosamente');
      setModalContratoAbierto(false);
      setArchivoContrato(null);
      
      if (reservaDetalle && reservaDetalle.id_reserva === reservaParaContrato.id_reserva) {
        setReservaDetalle(reservaActualizada);
      }
      
      await cargarReservas();
      
      if (modalDetalleAbierto) {
        setTimeout(() => abrirModalDetalle(reservaActualizada), 300);
      }
    } catch (error) {
      toast.error('Error al subir el contrato');
    } finally {
      setSubiendoContrato(false);
    }
  };

  const abrirModalSubirArchivo = () => {
    setArchivoAdicional(null);
    setNombreArchivoAdicional('');
    setModalSubirArchivo(true);
  };

  const subirArchivoAdicional = async () => {
    if (!archivoAdicional || !reservaDetalle) return;
    const archivosActuales = reservaDetalle.archivos_adjuntos || [];
    if (archivosActuales.length >= 5) {
      toast.error('Máximo 5 archivos por reserva');
      return;
    }

    try {
      setSubiendoArchivoAdicional(true);
      const formDataUpload = new FormData();
      formDataUpload.append('file', archivoAdicional);
      
      const url = nombreArchivoAdicional 
        ? `${API}/reservas/${reservaDetalle.id_reserva}/archivos?nombre=${encodeURIComponent(nombreArchivoAdicional)}`
        : `${API}/reservas/${reservaDetalle.id_reserva}/archivos`;
      
      const response = await axios.post(url, formDataUpload, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      setReservaDetalle({
        ...reservaDetalle,
        archivos_adjuntos: [...archivosActuales, response.data]
      });

      toast.success('Archivo subido correctamente');
      setModalSubirArchivo(false);
      setArchivoAdicional(null);
      setNombreArchivoAdicional('');
      cargarReservas();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al subir el archivo');
    } finally {
      setSubiendoArchivoAdicional(false);
    }
  };

  const eliminarArchivoAdicional = async (archivoId) => {
    if (!reservaDetalle) return;
    try {
      await axios.delete(`${API}/reservas/${reservaDetalle.id_reserva}/archivos/${archivoId}`);
      const archivosActualizados = (reservaDetalle.archivos_adjuntos || []).filter(a => a.id !== archivoId);
      setReservaDetalle({ ...reservaDetalle, archivos_adjuntos: archivosActualizados });
      toast.success('Archivo eliminado');
      cargarReservas();
    } catch (error) {
      toast.error('Error al eliminar el archivo');
    }
  };

  const abrirPreviewDocumento = (documento) => {
    setDocumentoPreview(documento);
    setModalPreviewDocumento(true);
  };

  const descargarDocumento = (url, nombre) => {
    const link = document.createElement('a');
    link.href = `${BACKEND_URL}${url}`;
    link.download = nombre;
    link.target = '_blank';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // Message operations
  const iniciarEdicionNombreMensaje = (mensaje) => {
    setEditandoNombreMensaje(mensaje.id_mensaje);
    setNombreMensajeTemporal(mensaje.nombre_mensaje || mensaje.plantilla_nombre || 'Mensaje sin nombre');
  };

  const guardarNombreMensaje = async (idMensaje) => {
    try {
      await axios.patch(`${API}/mensajes-enviados/${idMensaje}/nombre?nombre=${encodeURIComponent(nombreMensajeTemporal)}`);
      const mensajesActualizados = mensajesHistorial.map(m => 
        m.id_mensaje === idMensaje ? { ...m, nombre_mensaje: nombreMensajeTemporal } : m
      );
      setMensajesHistorial(mensajesActualizados);
      toast.success('Nombre actualizado');
      setEditandoNombreMensaje(null);
    } catch (error) {
      toast.error('Error al actualizar el nombre');
    }
  };

  const cancelarEdicionNombre = () => {
    setEditandoNombreMensaje(null);
    setNombreMensajeTemporal('');
  };

  const abrirModalAsociarReserva = (mensaje) => {
    setMensajeAsociar(mensaje);
    setReservaSeleccionadaAsociar('');
    setModalAsociarReserva(true);
  };

  const asociarMensajeReserva = async () => {
    if (!mensajeAsociar || !reservaSeleccionadaAsociar) return;
    try {
      await axios.patch(`${API}/mensajes-enviados/${mensajeAsociar.id_mensaje}/reserva?reserva_id=${reservaSeleccionadaAsociar}`);
      toast.success('Mensaje asociado a la reserva');
      setModalAsociarReserva(false);
      const mensajesResponse = await axios.get(`${API}/reservas/${reservaDetalle.id_reserva}/mensajes`);
      setMensajesHistorial(mensajesResponse.data);
    } catch (error) {
      toast.error('Error al asociar el mensaje');
    }
  };

  const eliminarMensajeHistorial = async () => {
    if (!mensajeEliminar) return;
    try {
      await axios.delete(`${API}/mensajes-enviados/${mensajeEliminar.id_mensaje}`);
      setMensajesHistorial(mensajesHistorial.filter(m => m.id_mensaje !== mensajeEliminar.id_mensaje));
      toast.success('Mensaje eliminado');
      setMensajeEliminar(null);
    } catch (error) {
      toast.error('Error al eliminar el mensaje');
    }
  };

  // Navigation
  const irACrearContrato = (reserva) => {
    const clienteAsociado = clientes.find(
      c => c.email && reserva.huesped_email && c.email.toLowerCase() === reserva.huesped_email.toLowerCase()
    ) || clientes.find(
      c => `${c.nombre} ${c.apellido || ''}`.trim().toLowerCase() === reserva.huesped_nombre.toLowerCase()
    );

    if (!clienteAsociado) {
      toast.error('No se encontró el cliente asociado a esta reserva.');
      return;
    }
    navigate(`/contratos?reserva=${reserva.id_reserva}&cliente=${clienteAsociado.id_cliente}&generar=true`);
  };

  const enviarWhatsApp = (reserva) => {
    navigate('/mensajes-whatsapp', {
      state: {
        clientePreseleccionado: reserva.cliente_id,
        nombreCliente: reserva.huesped_nombre,
        telefonoCliente: reserva.huesped_telefono,
        reservaId: reserva.id_reserva
      }
    });
  };

  if (!propiedadSeleccionada) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <AlertTriangle className="w-12 h-12 text-[#FF7F6A] mx-auto mb-4" />
          <p className="text-[#0E3A53] text-lg">Selecciona una propiedad para ver las reservas</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="reservas-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-3xl font-bold text-[#0E3A53]" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
            Reservas
          </h1>
          <p className="text-[#0E3A53]/60 mt-1">
            Gestiona las reservas de {propiedadSeleccionada.nombre}
          </p>
        </div>
        <Button 
          onClick={() => abrirModal()}
          className="bg-gradient-to-r from-[#1AA1D8] to-[#0E3A53] hover:opacity-90"
          data-testid="new-reserva-btn"
        >
          <Plus className="w-5 h-5 mr-2" />
          Nueva Reserva
        </Button>
      </div>

      {/* Filters */}
      <ReservaFilters
        busqueda={busqueda}
        setBusqueda={setBusqueda}
        filtroAnio={filtroAnio}
        setFiltroAnio={setFiltroAnio}
        filtroEstado={filtroEstado}
        setFiltroEstado={setFiltroEstado}
        limpiarFiltros={limpiarFiltros}
        aniosDisponibles={aniosDisponibles}
        estadosReserva={estadosReserva}
        totalReservas={reservas.length}
        reservasFiltradas={reservasFiltradas.length}
      />

      {/* Indicador de filtro por fecha desde Dashboard */}
      {(filtroFechaDesde || filtroFechaHasta) && (
        <div className="flex items-center justify-between bg-[#1AA1D8]/10 border border-[#1AA1D8]/30 rounded-lg px-4 py-3">
          <div className="flex items-center gap-2">
            <Calendar className="w-5 h-5 text-[#1AA1D8]" />
            <span className="text-sm text-[#0E3A53]">
              Mostrando reservas del período: 
              <span className="font-semibold ml-1">
                {filtroFechaDesde && new Date(filtroFechaDesde + 'T12:00:00').toLocaleDateString('es-CL', { month: 'long', year: 'numeric' })}
              </span>
            </span>
          </div>
          <Button
            variant="ghost"
            size="sm"
            onClick={limpiarFiltros}
            className="text-[#1AA1D8] hover:text-[#0E3A53]"
          >
            <X className="w-4 h-4 mr-1" />
            Quitar filtro
          </Button>
        </div>
      )}

      {/* Table */}
      <div className="card-beach">
        <ReservaTable
          reservas={reservasFiltradas}
          canales={canales}
          estadosReserva={estadosReserva}
          onVerDetalle={abrirModalDetalle}
          onEditar={abrirModal}
          onEliminar={confirmarEliminacion}
          onCrearContrato={irACrearContrato}
          onEnviarWhatsApp={enviarWhatsApp}
        />
      </div>

      {/* Modals */}
      <ReservaForm
        open={modalAbierto}
        onClose={() => setModalAbierto(false)}
        formData={formData}
        setFormData={setFormData}
        reservaEditando={reservaEditando}
        clientes={clientes}
        canales={canales}
        estadosReserva={estadosReserva}
        categorias={categorias}
        proveedores={proveedores}
        clienteSeleccionado={clienteSeleccionado}
        onSeleccionarCliente={seleccionarCliente}
        onAbrirNuevoCliente={abrirModalNuevoCliente}
        gastosReserva={gastosReserva}
        onAgregarGasto={agregarGastoReserva}
        onActualizarGasto={actualizarGastoReserva}
        onEliminarGasto={eliminarGastoReserva}
        onGuardar={guardarReserva}
        obtenerProveedoresPorCategoria={obtenerProveedoresPorCategoria}
      />

      <ClienteFormModal
        open={modalClienteAbierto}
        onClose={() => setModalClienteAbierto(false)}
        nuevoCliente={nuevoCliente}
        setNuevoCliente={setNuevoCliente}
        onGuardar={guardarNuevoCliente}
      />

      <ContratoUploadModal
        open={modalContratoAbierto}
        onClose={() => setModalContratoAbierto(false)}
        reserva={reservaParaContrato}
        archivo={archivoContrato}
        setArchivo={setArchivoContrato}
        subiendo={subiendoContrato}
        onSubir={subirContratoFirmado}
        formatDate={formatDate}
      />

      <ArchivoUploadModal
        open={modalSubirArchivo}
        onClose={() => setModalSubirArchivo(false)}
        archivo={archivoAdicional}
        setArchivo={setArchivoAdicional}
        nombre={nombreArchivoAdicional}
        setNombre={setNombreArchivoAdicional}
        subiendo={subiendoArchivoAdicional}
        onSubir={subirArchivoAdicional}
      />

      <DocumentoPreviewModal
        open={modalPreviewDocumento}
        onClose={() => setModalPreviewDocumento(false)}
        documento={documentoPreview}
      />

      {/* Delete Confirmation */}
      <AlertDialog open={!!reservaAEliminar} onOpenChange={() => setReservaAEliminar(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>¿Eliminar reserva?</AlertDialogTitle>
            <AlertDialogDescription>
              Esta acción no se puede deshacer. Se eliminará permanentemente la reserva de{' '}
              <strong>{reservaAEliminar?.huesped_nombre}</strong>.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={eliminarReserva} className="bg-red-600 hover:bg-red-700">
              Eliminar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Delete Message Confirmation */}
      <AlertDialog open={!!mensajeEliminar} onOpenChange={() => setMensajeEliminar(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>¿Eliminar mensaje del historial?</AlertDialogTitle>
            <AlertDialogDescription>
              Se eliminará este mensaje del historial de la reserva. El mensaje original no se verá afectado.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={eliminarMensajeHistorial} className="bg-red-600 hover:bg-red-700">
              Eliminar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Associate Message Modal */}
      <Dialog open={modalAsociarReserva} onOpenChange={setModalAsociarReserva}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Asociar mensaje a otra reserva</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <Select value={reservaSeleccionadaAsociar} onValueChange={setReservaSeleccionadaAsociar}>
              <SelectTrigger>
                <SelectValue placeholder="Seleccionar reserva..." />
              </SelectTrigger>
              <SelectContent>
                {reservas.filter(r => r.id_reserva !== reservaDetalle?.id_reserva).map(r => (
                  <SelectItem key={r.id_reserva} value={r.id_reserva}>
                    {r.huesped_nombre} - {formatDate(r.fecha_check_in)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <div className="flex gap-2 justify-end">
              <Button variant="outline" onClick={() => setModalAsociarReserva(false)}>Cancelar</Button>
              <Button onClick={asociarMensajeReserva} disabled={!reservaSeleccionadaAsociar}>Asociar</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Detail Modal - Keeping inline due to complexity */}
      <Dialog open={modalDetalleAbierto} onOpenChange={setModalDetalleAbierto}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-3xl font-bold text-[#0E3A53]" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
              Detalle de Reserva
            </DialogTitle>
          </DialogHeader>

          {reservaDetalle && (
            <div className="space-y-6">
              {/* Guest Info */}
              <div className="card-beach bg-gradient-to-r from-[#1AA1D8]/10 to-[#8FE0FF]/10">
                <h3 className="text-xl font-bold text-[#0E3A53] mb-4 flex items-center gap-2">
                  <MessageCircle className="w-5 h-5" />
                  Información del Huésped
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label className="text-[#0E3A53]/70">Nombre</Label>
                    <p className="font-semibold text-[#0E3A53] text-lg">{reservaDetalle.huesped_nombre}</p>
                  </div>
                  <div>
                    <Label className="text-[#0E3A53]/70">Teléfono</Label>
                    <p className="font-semibold text-[#0E3A53]">{reservaDetalle.huesped_telefono || 'No especificado'}</p>
                  </div>
                  <div className="md:col-span-2">
                    <Label className="text-[#0E3A53]/70">Email</Label>
                    <p className="font-semibold text-[#0E3A53]">{reservaDetalle.huesped_email || 'No especificado'}</p>
                  </div>
                </div>
              </div>

              {/* Dates and Status */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="card-beach">
                  <h3 className="text-lg font-bold text-[#0E3A53] mb-3 flex items-center gap-2">
                    <Calendar className="w-5 h-5" />
                    Fechas
                  </h3>
                  <div className="space-y-3">
                    <div>
                      <Label className="text-[#0E3A53]/70">Check-in</Label>
                      <p className="font-semibold text-[#0E3A53]">{formatDate(reservaDetalle.fecha_check_in)}</p>
                    </div>
                    <div>
                      <Label className="text-[#0E3A53]/70">Check-out</Label>
                      <p className="font-semibold text-[#0E3A53]">{formatDate(reservaDetalle.fecha_check_out)}</p>
                    </div>
                    <div>
                      <Label className="text-[#0E3A53]/70">Duración</Label>
                      <p className="font-semibold text-[#1AA1D8] text-lg">{reservaDetalle.noches} noches</p>
                    </div>
                  </div>
                </div>

                <div className="card-beach">
                  <h3 className="text-lg font-bold text-[#0E3A53] mb-3">Estado y Canal</h3>
                  <div className="space-y-3">
                    <div>
                      <Label className="text-[#0E3A53]/70">Estado Actual</Label>
                      {editandoEstado ? (
                        <div className="space-y-2 mt-2">
                          <Select value={nuevoEstado} onValueChange={setNuevoEstado}>
                            <SelectTrigger className="w-full">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              {estadosReserva.map(estado => (
                                <SelectItem key={estado.id_estado} value={estado.nombre}>
                                  <div className="flex items-center gap-2">
                                    <span className="w-3 h-3 rounded-full" style={{ backgroundColor: estado.color }}></span>
                                    {estado.nombre}
                                  </div>
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                          <div className="flex gap-2">
                            <Button onClick={actualizarEstadoReserva} size="sm" className="bg-[#1AA1D8]">Guardar</Button>
                            <Button onClick={() => setEditandoEstado(false)} size="sm" variant="outline">Cancelar</Button>
                          </div>
                        </div>
                      ) : (
                        <div className="flex items-center gap-2 mt-2">
                          {(() => {
                            const colores = getColorEstado(reservaDetalle.estado_reserva);
                            return (
                              <span className="px-3 py-1 rounded-full text-sm font-medium" style={{ backgroundColor: colores.fondo, color: colores.texto }}>
                                {reservaDetalle.estado_reserva}
                              </span>
                            );
                          })()}
                          <Button onClick={() => { setNuevoEstado(reservaDetalle.estado_reserva); setEditandoEstado(true); }} size="sm" variant="ghost" className="text-[#1AA1D8]">
                            <Edit className="w-4 h-4" />
                          </Button>
                        </div>
                      )}
                    </div>
                    <div>
                      <Label className="text-[#0E3A53]/70">Canal</Label>
                      {editandoCanal ? (
                        <div className="space-y-2 mt-2">
                          <Select value={nuevoCanal} onValueChange={setNuevoCanal}>
                            <SelectTrigger className="w-full">
                              <SelectValue placeholder="Seleccionar canal" />
                            </SelectTrigger>
                            <SelectContent>
                              {canales.map(canal => (
                                <SelectItem key={canal.id_canal} value={canal.id_canal}>{canal.nombre}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                          <div className="flex gap-2">
                            <Button onClick={actualizarCanalReserva} size="sm" className="bg-[#1AA1D8]">Guardar</Button>
                            <Button onClick={() => setEditandoCanal(false)} size="sm" variant="outline">Cancelar</Button>
                          </div>
                        </div>
                      ) : (
                        <div className="flex items-center gap-2 mt-2">
                          {(() => {
                            const canal = canales.find(c => c.id_canal === reservaDetalle.canal_id);
                            return canal ? (
                              <span className="px-3 py-1 rounded-full text-sm font-medium" style={{ backgroundColor: `${canal.color}20`, color: canal.color }}>
                                {canal.nombre}
                              </span>
                            ) : <span className="text-[#0E3A53]/50 text-sm">Sin canal</span>;
                          })()}
                          <Button onClick={() => { setNuevoCanal(reservaDetalle.canal_id || ''); setEditandoCanal(true); }} size="sm" variant="ghost" className="text-[#1AA1D8]">
                            <Edit className="w-4 h-4" />
                          </Button>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              {/* Financial Info */}
              <div className="card-beach bg-gradient-to-r from-green-500/10 to-emerald-500/10">
                <h3 className="text-lg font-bold text-[#0E3A53] mb-3 flex items-center gap-2">
                  <DollarSign className="w-5 h-5" />
                  Información Financiera
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <Label className="text-[#0E3A53]/70">Monto Bruto</Label>
                    <p className="font-bold text-[#0E3A53] text-xl">{formatCurrency(reservaDetalle.monto_bruto)}</p>
                  </div>
                  <div>
                    <Label className="text-[#0E3A53]/70">Comisiones</Label>
                    <p className="font-bold text-red-600 text-xl">-{formatCurrency(reservaDetalle.comisiones)}</p>
                  </div>
                  <div>
                    <Label className="text-[#0E3A53]/70">Monto Neto</Label>
                    <p className="font-bold text-green-600 text-xl">{formatCurrency(reservaDetalle.monto_neto)}</p>
                  </div>
                </div>
                
                {/* Abonos Section */}
                {reservaDetalle.estado_reserva?.toLowerCase().includes('abonado') && (
                  <div className="mt-4 p-4 bg-blue-50 rounded-lg border-2 border-blue-200">
                    <div className="flex items-center justify-between mb-3">
                      <Label className="text-blue-800 font-semibold">Historial de Abonos</Label>
                    </div>
                    
                    {reservaDetalle.abonos && reservaDetalle.abonos.length > 0 ? (
                      <div className="space-y-2 mb-4">
                        {reservaDetalle.abonos.map((abono, index) => (
                          <div key={abono.id_abono} className="flex items-center gap-3 p-2 bg-white rounded-lg border border-blue-200">
                            {editandoAbonoId === abono.id_abono ? (
                              <>
                                <Input type="date" defaultValue={abono.fecha} className="w-36" id={`fecha-abono-${abono.id_abono}`} />
                                <Input type="number" defaultValue={abono.monto} className="w-32" id={`monto-abono-${abono.id_abono}`} />
                                <Button size="sm" onClick={() => {
                                  const fecha = document.getElementById(`fecha-abono-${abono.id_abono}`).value;
                                  const monto = Number(document.getElementById(`monto-abono-${abono.id_abono}`).value);
                                  editarAbono(abono.id_abono, monto, fecha);
                                }} className="bg-blue-600 hover:bg-blue-700">✓</Button>
                                <Button size="sm" variant="outline" onClick={() => setEditandoAbonoId(null)}>✕</Button>
                              </>
                            ) : (
                              <>
                                <span className="text-sm text-blue-700 font-medium w-8">#{index + 1}</span>
                                <span className="text-sm text-gray-600 w-28">{new Date(abono.fecha + 'T12:00:00').toLocaleDateString('es-CL')}</span>
                                <span className="text-sm font-bold text-blue-800 flex-1">{formatCurrency(abono.monto)}</span>
                                <button onClick={() => setEditandoAbonoId(abono.id_abono)} className="p-1 hover:bg-blue-100 rounded">
                                  <Edit className="w-4 h-4 text-blue-600" />
                                </button>
                                <button onClick={() => eliminarAbono(abono.id_abono)} className="p-1 hover:bg-red-100 rounded">
                                  <Trash2 className="w-4 h-4 text-red-500" />
                                </button>
                              </>
                            )}
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-sm text-blue-600/70 mb-4">No hay abonos registrados</p>
                    )}
                    
                    <div className="p-3 bg-white rounded-lg border-2 border-dashed border-blue-300">
                      <Label className="text-sm text-blue-700 mb-2 block">Agregar Nuevo Abono</Label>
                      <div className="flex gap-2 items-end">
                        <div className="flex-1">
                          <Label className="text-xs text-blue-600">Fecha</Label>
                          <Input type="date" value={nuevoAbono.fecha} onChange={(e) => setNuevoAbono({ ...nuevoAbono, fecha: e.target.value })} />
                        </div>
                        <div className="flex-1">
                          <Label className="text-xs text-blue-600">Monto</Label>
                          <Input type="number" value={nuevoAbono.monto} onChange={(e) => setNuevoAbono({ ...nuevoAbono, monto: Number(e.target.value) })} placeholder="$0" />
                        </div>
                        <Button onClick={agregarAbono} className="bg-blue-600 hover:bg-blue-700">+ Agregar</Button>
                      </div>
                    </div>
                    
                    {(() => {
                      const totalAbonos = calcularTotalAbonos(reservaDetalle.abonos);
                      const saldoPendiente = reservaDetalle.monto_neto - totalAbonos;
                      const porcentaje = reservaDetalle.monto_neto > 0 ? Math.round((totalAbonos / reservaDetalle.monto_neto) * 100) : 0;
                      return (
                        <div className="grid grid-cols-3 gap-4 mt-4 pt-4 border-t border-blue-200">
                          <div>
                            <Label className="text-blue-700/70 text-sm">Total Abonado</Label>
                            <p className="font-bold text-blue-800 text-lg">{formatCurrency(totalAbonos)}</p>
                          </div>
                          <div>
                            <Label className="text-blue-700/70 text-sm">Porcentaje</Label>
                            <p className="font-bold text-blue-800 text-lg">{porcentaje}%</p>
                          </div>
                          <div>
                            <Label className="text-blue-700/70 text-sm">Saldo Pendiente</Label>
                            <p className={`font-bold text-lg ${saldoPendiente > 0 ? 'text-orange-600' : 'text-green-600'}`}>{formatCurrency(saldoPendiente)}</p>
                          </div>
                        </div>
                      );
                    })()}
                  </div>
                )}
              </div>

              {/* Documents */}
              <div className="card-beach">
                <h3 className="text-lg font-bold text-[#0E3A53] mb-4 flex items-center gap-2">
                  <FileCheck className="w-5 h-5" />
                  Documentos del Huésped
                </h3>
                <div className="space-y-3">
                  {/* Cédula de la reserva (desde solicitud) */}
                  {reservaDetalle?.cedula_url ? (
                    <div className="flex items-center justify-between p-3 bg-purple-500/10 rounded-lg border-2 border-purple-500/30">
                      <div className="flex items-center gap-3">
                        <div className="p-2 bg-purple-500/20 rounded-lg"><FileText className="w-5 h-5 text-purple-700" /></div>
                        <div><p className="font-medium text-[#0E3A53]">Cédula (Solicitud de Reserva)</p><p className="text-xs text-[#0E3A53]/60">Adjuntado desde el formulario público</p></div>
                      </div>
                      <div className="flex gap-2">
                        <button onClick={() => abrirPreviewDocumento({ url: reservaDetalle.cedula_url, nombre: 'Cédula de Identidad', tipo: 'cedula' })} className="flex items-center gap-2 px-3 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 text-sm">
                          <Eye className="w-4 h-4" />Vista Previa
                        </button>
                        <button onClick={() => descargarDocumento(reservaDetalle.cedula_url, 'cedula_solicitud.pdf')} className="flex items-center gap-2 px-3 py-2 bg-purple-100 text-purple-700 rounded-lg hover:bg-purple-200 text-sm">
                          <Download className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  ) : null}
                  
                  {/* Cédula del cliente */}
                  {clienteDetalle?.cedula_url ? (
                    <div className="flex items-center justify-between p-3 bg-green-500/10 rounded-lg border-2 border-green-500/30">
                      <div className="flex items-center gap-3">
                        <div className="p-2 bg-green-500/20 rounded-lg"><FileText className="w-5 h-5 text-green-700" /></div>
                        <div><p className="font-medium text-[#0E3A53]">Cédula de Identidad</p><p className="text-xs text-[#0E3A53]/60">Documento del cliente</p></div>
                      </div>
                      <div className="flex gap-2">
                        <button onClick={() => abrirPreviewDocumento({ url: clienteDetalle.cedula_url, nombre: 'Cédula de Identidad', tipo: 'cedula' })} className="flex items-center gap-2 px-3 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 text-sm">
                          <Eye className="w-4 h-4" />Vista Previa
                        </button>
                        <button onClick={() => descargarDocumento(clienteDetalle.cedula_url, 'cedula.pdf')} className="flex items-center gap-2 px-3 py-2 bg-green-100 text-green-700 rounded-lg hover:bg-green-200 text-sm">
                          <Download className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  ) : !reservaDetalle?.cedula_url && (
                    <div className="p-3 bg-gray-100 rounded-lg border-2 border-gray-200">
                      <div className="flex items-center gap-3">
                        <div className="p-2 bg-gray-200 rounded-lg"><FileText className="w-5 h-5 text-gray-400" /></div>
                        <div><p className="font-medium text-gray-600">Cédula de Identidad</p><p className="text-xs text-gray-500">No adjuntada</p></div>
                      </div>
                    </div>
                  )}

                  {reservaDetalle.contrato_firmado_url ? (
                    <div className="flex items-center justify-between p-3 bg-[#1AA1D8]/10 rounded-lg border-2 border-[#1AA1D8]">
                      <div className="flex items-center gap-3">
                        <div className="p-2 bg-[#1AA1D8]/20 rounded-lg"><FileCheck className="w-5 h-5 text-[#1AA1D8]" /></div>
                        <div><p className="font-medium text-[#0E3A53]">Contrato Firmado</p><p className="text-xs text-[#0E3A53]/60">Documento de la reserva</p></div>
                      </div>
                      <div className="flex gap-2">
                        <button onClick={() => abrirPreviewDocumento({ url: reservaDetalle.contrato_firmado_url, nombre: 'Contrato Firmado', tipo: 'contrato' })} className="flex items-center gap-2 px-3 py-2 bg-[#1AA1D8] text-white rounded-lg hover:bg-[#0E3A53] text-sm">
                          <Eye className="w-4 h-4" />Vista Previa
                        </button>
                        <button onClick={() => descargarDocumento(reservaDetalle.contrato_firmado_url, 'contrato_firmado.pdf')} className="flex items-center gap-2 px-3 py-2 bg-blue-100 text-blue-700 rounded-lg hover:bg-blue-200 text-sm">
                          <Download className="w-4 h-4" />
                        </button>
                        <Button onClick={() => { setModalDetalleAbierto(false); abrirModalContrato(reservaDetalle); }} size="sm" variant="outline" className="border-[#1AA1D8] text-[#1AA1D8]">
                          <Upload className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  ) : (
                    <div className="p-3 bg-gray-100 rounded-lg border-2 border-gray-200">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <div className="p-2 bg-gray-200 rounded-lg"><FileCheck className="w-5 h-5 text-gray-400" /></div>
                          <div><p className="font-medium text-gray-600">Contrato Firmado</p><p className="text-xs text-gray-500">No adjuntado</p></div>
                        </div>
                        <Button onClick={() => { setModalDetalleAbierto(false); abrirModalContrato(reservaDetalle); }} size="sm" className="bg-[#1AA1D8]">
                          <Upload className="w-4 h-4 mr-2" />Subir
                        </Button>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Additional Files */}
              <div className="card-beach bg-gradient-to-r from-amber-50 to-yellow-50">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-bold text-[#0E3A53] flex items-center gap-2">
                    <Upload className="w-5 h-5" />
                    Archivos Adicionales ({(reservaDetalle.archivos_adjuntos || []).length}/5)
                  </h3>
                  {(reservaDetalle.archivos_adjuntos || []).length < 5 && (
                    <Button onClick={abrirModalSubirArchivo} size="sm" className="bg-amber-600 hover:bg-amber-700">
                      <Plus className="w-4 h-4 mr-1" />Subir Archivo
                    </Button>
                  )}
                </div>

                {!reservaDetalle.archivos_adjuntos || reservaDetalle.archivos_adjuntos.length === 0 ? (
                  <div className="text-center py-6 text-[#0E3A53]/60">
                    <FileText className="w-12 h-12 mx-auto mb-2 text-[#0E3A53]/30" />
                    <p className="text-sm">No hay archivos adicionales adjuntos</p>
                    <p className="text-xs text-[#0E3A53]/50 mt-1">Puedes subir hasta 5 archivos</p>
                  </div>
                ) : (
                  <div className="space-y-2">
                    {reservaDetalle.archivos_adjuntos.map((archivo) => (
                      <div key={archivo.id} className="flex items-center justify-between p-3 bg-white rounded-lg border-2 border-amber-200 hover:border-amber-300">
                        <div className="flex items-center gap-3 flex-1 min-w-0">
                          <div className="p-2 bg-amber-100 rounded-lg"><FileText className="w-5 h-5 text-amber-700" /></div>
                          <div className="flex-1 min-w-0">
                            <p className="font-medium text-[#0E3A53] truncate">{archivo.nombre}</p>
                            <p className="text-xs text-gray-500">{new Date(archivo.fecha_subida).toLocaleDateString('es-CL', { day: '2-digit', month: 'short', year: 'numeric' })}</p>
                          </div>
                        </div>
                        <div className="flex gap-2 flex-shrink-0">
                          <button onClick={() => abrirPreviewDocumento({ url: archivo.url, nombre: archivo.nombre, tipo: 'archivo_adicional' })} className="p-2 hover:bg-amber-100 rounded-lg" title="Vista previa">
                            <Eye className="w-4 h-4 text-amber-700" />
                          </button>
                          <button onClick={() => descargarDocumento(archivo.url, archivo.nombre)} className="p-2 hover:bg-amber-100 rounded-lg" title="Descargar">
                            <Download className="w-4 h-4 text-amber-700" />
                          </button>
                          <button onClick={() => eliminarArchivoAdicional(archivo.id)} className="p-2 hover:bg-red-100 rounded-lg" title="Eliminar">
                            <Trash2 className="w-4 h-4 text-red-600" />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Notes */}
              {reservaDetalle.notas && (
                <div className="card-beach bg-[#F7E8C9]/30">
                  <h3 className="text-lg font-bold text-[#0E3A53] mb-2">Notas</h3>
                  <p className="text-[#0E3A53] whitespace-pre-wrap">{reservaDetalle.notas}</p>
                </div>
              )}

              {/* Message History */}
              <div className="card-beach bg-gradient-to-r from-blue-50 to-indigo-50">
                <h3 className="text-lg font-bold text-[#0E3A53] mb-4 flex items-center gap-2">
                  <Send className="w-5 h-5" />
                  Historial de Mensajes Enviados
                </h3>
                
                {mensajesHistorial.length === 0 ? (
                  <div className="text-center py-6 text-[#0E3A53]/60">
                    <MessageCircle className="w-12 h-12 mx-auto mb-2 text-[#0E3A53]/30" />
                    <p className="text-sm">No se han enviado mensajes para esta reserva</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {mensajesHistorial.map((mensaje) => {
                      const fechaEnvio = new Date(mensaje.fecha_envio);
                      const nombreMostrar = mensaje.nombre_mensaje || mensaje.plantilla_nombre || 'Mensaje sin nombre';
                      
                      return (
                        <div key={mensaje.id_mensaje} className="bg-white rounded-lg p-4 border-2 border-blue-200 hover:border-blue-300">
                          <div className="flex items-start justify-between gap-3">
                            <div className="flex-1 min-w-0">
                              {editandoNombreMensaje === mensaje.id_mensaje ? (
                                <div className="flex items-center gap-2 mb-2">
                                  <Input value={nombreMensajeTemporal} onChange={(e) => setNombreMensajeTemporal(e.target.value)} className="flex-1" placeholder="Nombre del mensaje" autoFocus onKeyDown={(e) => { if (e.key === 'Enter') guardarNombreMensaje(mensaje.id_mensaje); if (e.key === 'Escape') cancelarEdicionNombre(); }} />
                                  <Button onClick={() => guardarNombreMensaje(mensaje.id_mensaje)} size="sm" className="bg-green-600 hover:bg-green-700"><Check className="w-4 h-4" /></Button>
                                  <Button onClick={cancelarEdicionNombre} size="sm" variant="outline"><X className="w-4 h-4" /></Button>
                                </div>
                              ) : (
                                <div className="flex items-center gap-2 mb-2">
                                  <h4 className="font-semibold text-[#0E3A53] text-base">{nombreMostrar}</h4>
                                  <button onClick={() => iniciarEdicionNombreMensaje(mensaje)} className="p-1 hover:bg-blue-100 rounded" title="Editar nombre">
                                    <Edit className="w-3.5 h-3.5 text-blue-600" />
                                  </button>
                                </div>
                              )}
                              
                              <div className="flex items-center gap-3 text-sm text-gray-600 mb-2">
                                <span className="flex items-center gap-1">
                                  <Calendar className="w-3.5 h-3.5" />
                                  {fechaEnvio.toLocaleDateString('es-CL', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
                                </span>
                                <span className="flex items-center gap-1">
                                  <MessageCircle className="w-3.5 h-3.5" />
                                  {mensaje.total_destinatarios} destinatario{mensaje.total_destinatarios !== 1 ? 's' : ''}
                                </span>
                              </div>
                              
                              <p className="text-sm text-gray-700 line-clamp-2">{mensaje.contenido}</p>
                            </div>
                            
                            <div className="flex items-center gap-1 flex-shrink-0">
                              <button onClick={() => abrirModalAsociarReserva(mensaje)} className="p-2 hover:bg-purple-100 rounded-lg" title="Asociar a otra reserva">
                                <Link className="w-4 h-4 text-purple-600" />
                              </button>
                              <a href={`/mensajes-whatsapp?mensaje_id=${mensaje.id_mensaje}`} className="p-2 hover:bg-blue-100 rounded-lg" title="Ver mensaje completo">
                                <Eye className="w-4 h-4 text-blue-600" />
                              </a>
                              <button onClick={() => setMensajeEliminar(mensaje)} className="p-2 hover:bg-red-100 rounded-lg" title="Eliminar del historial">
                                <Trash2 className="w-4 h-4 text-red-600" />
                              </button>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>

              {/* Action Buttons */}
              <div className="flex flex-wrap gap-3 justify-end border-t pt-4">
                <Button onClick={() => enviarWhatsApp(reservaDetalle)} variant="outline" className="border-[#25D366] text-[#25D366]">
                  <MessageCircle className="w-4 h-4 mr-2" />Enviar WhatsApp
                </Button>
                <Button onClick={() => irACrearContrato(reservaDetalle)} variant="outline" className="border-[#0E3A53] text-[#0E3A53]">
                  <FileText className="w-4 h-4 mr-2" />Crear Contrato
                </Button>
                <Button onClick={() => { setModalDetalleAbierto(false); abrirModal(reservaDetalle); }} className="bg-gradient-to-r from-[#1AA1D8] to-[#0E3A53]">
                  <Edit className="w-4 h-4 mr-2" />Editar Reserva
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Reservas;
